<template>
  <div class="project-team-select mt-small">
    <slot />

    <ak-field label="Add a campaign team user" addons>
      <ak-select
        :model-value="selectedTeamMember"
        :options="teamOptions"
        track-by="_id"
        label="name"
        group-values="users"
        group-label="label"
        :custom-label="customMemberLabel"
        @select="selectMember"
      />
      <ak-button @click="addMember">Add User</ak-button>
    </ak-field>

    <template v-for="member in selectedTeam" :key="member._id">
      <ak-box narrow rounded class="mb-tiny bg-grey-lightest px-small">
        <ak-pack>
          <ak-pack-item class="flex__item">
            <template v-if="showSetPrimary">
              <ak-button
                v-if="member.campaignRole && !member.primaryInRole"
                class="pull-right mt-small"
                size="tiny"
                @click.stop.prevent="
                  setCampaignTeamUserAsPrimaryForRole(member)
                "
                >Set as primary</ak-button
              >
            </template>
            <ak-pack>
              <ak-pack-item>
                <ak-avatar :string="member.name" />
              </ak-pack-item>
              <ak-pack-item>
                <span class="block">{{ member.name }}</span>
                <strong v-if="member.campaignRole" class="block">
                  <span v-if="member.primaryInRole">Primary</span>
                  {{ formatRole(member.campaignRole) }}
                </strong>
                <strong v-else class="block">Client</strong>
              </ak-pack-item>
            </ak-pack>
          </ak-pack-item>
          <ak-tooltip>
            <template #trigger>
              <ak-button
                size="small"
                color="transparent"
                circle
                @click="removeMember(member)"
              >
                <ak-icon type="close" />
              </ak-button>
            </template>
            Remove from Campaign Team
          </ak-tooltip>
        </ak-pack>
      </ak-box>
    </template>
  </div>
</template>

<script>
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import PackItem from '@akerolabs/akero-vue/src/components/pack/PackItem.vue'
import Pack from '@akerolabs/akero-vue/src/components/pack/Pack.vue'
import Avatar from '@akerolabs/akero-vue/src/components/avatar/Avatar.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Tooltip from '@akerolabs/akero-vue/src/components/tooltip/Tooltip.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import { defineComponent, ref, onMounted, toRaw } from 'vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { accountObj, agencyObj } from '@akerolabs/akero-vue/src/store'
import { capitalizeFirstLetter, merge } from '@akerolabs/akero-vue/src'
import config from '../../config/index'

import isEmpty from 'lodash/isEmpty'
import filter from 'lodash/filter'

const { campaignRoles } = config

export default defineComponent({
  name: 'AkProjectTeamSelect',

  components: {
    [Field.name]: Field,
    [Select.name]: Select,
    [Button.name]: Button,
    [Avatar.name]: Avatar,
    [PackItem.name]: PackItem,
    [Pack.name]: Pack,
    [Box.name]: Box,
    [Tooltip.name]: Tooltip,
    [Icon.name]: Icon,
  },

  props: {
    team: {
      type: Array,
      default: () => [],
    },
    showSetPrimary: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['change'],

  setup(props, { emit }) {
    const agencyUserLevels = ['agencyuser', 'agencyadmin']
    const teamOptions = ref([])
    const selectedTeamMember = ref(undefined)
    const selectedTeam = ref([...props.team])
    const ready = ref(false)

    const removeMember = (member) => {
      const idx = selectedTeam.value.findIndex((i) => {
        return i.user === member.user
      })
      if (idx >= 0) {
        selectedTeam.value.splice(idx, 1)
        emit('change', toRaw(selectedTeam.value))
      }
    }

    const getUsers = async () => {
      const { _id: agencyId } = agencyObj()
      const { _id: accountId } = accountObj()

      const { data } = await request.get('/users', {
        params: {
          per_page: 0,
          deleted: JSON.stringify({ $ne: true }),
          filters: 'name agency accounts campaignRole department',
          agency: agencyId,
          $or: JSON.stringify([
            {
              campaignRole: { $exists: true, $ne: 'other' },
              accounts: {
                $elemMatch: {
                  status: 'active',
                  userLevel: { $in: agencyUserLevels },
                  agency: agencyId,
                },
              },
            },
            {
              accounts: {
                $elemMatch: {
                  status: 'active',
                  userLevel: {
                    $in: ['reporter', 'accountuser', 'accountadmin'],
                  },
                  account: accountId,
                },
              },
            },
          ]),
        },
      })

      return data.data
    }

    const buildUserList = (users) => {
      const { _id: agencyId } = agencyObj()
      const { _id: accountId } = accountObj()
      const userList = []

      users.forEach((user) => {
        const account = user.accounts.find((account) => {
          return (
            account.agency === agencyId &&
            (account.account === accountId ||
              agencyUserLevels.includes(account.userLevel))
          )
        })

        userList.push(
          Object.assign(
            {
              _id: user._id,
              name: user.name,
              profile: account._id,
              agency: account.agency,
              userLevel: account.userLevel,
              group: agencyUserLevels.includes(account.userLevel)
                ? 'Agency Users'
                : 'Account Users',
            },
            agencyUserLevels.includes(account.userLevel)
              ? { campaignRole: user.campaignRole, department: user.department }
              : {},
          ),
        )
      })

      return userList
    }

    const addUserFlags = (users) => {
      users.forEach((member) => {
        const user = selectedTeam.value.find((user) => {
          return user._id === member.user
        })
        if (user) {
          setUserFlags(member, user)
        }
      })

      return users
    }

    const setUserFlags = (member, user) => {
      if (
        user.agency === '52035c0b01bb5a9b5d130923' &&
        agencyUserLevels.includes(user.userLevel)
      ) {
        member.flagNN = true
        if (user.department === 'advertising') {
          member.flagJT = getJobFlag(user.campaignRole)
        }
      }
    }

    const getJobFlag = (name) => {
      console.log(campaignRoles)

      const entry = campaignRoles.find((role) => {
        return role.value === name
      })

      return (entry && entry.label) || ''
    }

    const campaignRoleGrouping = function (users) {
      const planning = { label: 'Planning', users: [] }
      const paidMedia = { label: 'Paid Media Analysts', users: [] }
      const rest = { label: 'All Other Agency Users', users: [] }
      const account = { label: 'Account Users', users: [] }
      const list = [planning, paidMedia, rest, account]

      users.forEach(function (user) {
        if (user.group === 'Agency Users') {
          if (user.campaignRole) {
            switch (user.campaignRole) {
              case 'planning':
                user.campaignRoleTitle = 'Planners'
                planning.users.push(user)
                break
              case 'paid_media':
                user.campaignRoleTitle = 'Paid Media Analysts'
                paidMedia.users.push(user)
                break
              default:
                user.campaignRoleTitle = 'All Other Agency Users'
                rest.users.push(user)
                break
            }
          } else {
            user.campaignRoleTitle = 'All Other Agency Users'
            rest.users.push(user)
          }
        }
        if (user.group === 'Account Users') {
          user.campaignRoleTitle = 'Account Users'
          account.users.push(user)
        }
      })

      return list
    }

    const addMember = () => {
      const user = selectedTeamMember.value

      const entry = selectedTeam.value.find((member) => {
        return member.user === user._id
      })

      if (entry) {
        return
      }

      const usersInRole = filter(selectedTeam.value, {
        campaignRole: user.campaignRole,
      })

      const member = {
        user: user._id,
        account: user.profile,
        name: user.name,
        campaignRole: user.campaignRole,
        campaignRoleTitle: user.campaignRoleTitle,
        primaryInRole:
          !!user.campaignRole &&
          isEmpty(
            filter(usersInRole, {
              primaryInRole: true,
            }),
          ),
      }

      setUserFlags(member, user)
      selectedTeam.value.push(member)
      sendUpdate(selectedTeam.value)
    }

    const selectMember = (user) => {
      selectedTeamMember.value = user
    }

    const customMemberLabel = (user) => {
      return user.name
    }

    const setCampaignTeamUserAsPrimaryForRole = (user) => {
      selectedTeam.value = selectedTeam.value.map((entry) => {
        return !entry.campaignRole || entry.campaignRole !== user.campaignRole
          ? entry
          : merge(entry, {
              primaryInRole:
                entry.campaignRole === user.campaignRole &&
                user.user === entry.user,
            })
      })
    }

    const formatRole = (role) => {
      return role
        .split('_')
        .map((i) => capitalizeFirstLetter(i))
        .join(' ')
    }

    const sendUpdate = (list) => {
      emit('change', toRaw(list))
      selectedTeamMember.value = null
    }

    onMounted(async () => {
      const users = await getUsers()
      const userList = campaignRoleGrouping(addUserFlags(buildUserList(users)))
      teamOptions.value = userList
      ready.value = true
    })

    return {
      setCampaignTeamUserAsPrimaryForRole,
      teamOptions,
      selectedTeamMember,
      selectedTeam,
      removeMember,
      addMember,
      customMemberLabel,
      ready,
      selectMember,
      formatRole,
    }
  },
})
</script>
